import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon, CheckCircleIcon, WifiIcon } from '@heroicons/react/24/outline';

interface AdvancedRouterModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const AdvancedRouterModal: React.FC<AdvancedRouterModalProps> = ({ isOpen, onClose }) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-50 z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            onClick={onClose}
          >
            <div
              className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-airmax-blue to-blue-600 text-white p-6 rounded-t-2xl relative">
                <button
                  onClick={onClose}
                  className="absolute top-4 right-4 text-white hover:bg-white/20 rounded-full p-2 transition-colors"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
                <div className="flex items-center space-x-4">
                  <div className="bg-white/20 p-3 rounded-full">
                    <WifiIcon className="h-8 w-8" />
                  </div>
                  <div>
                    <h2 className="text-3xl font-bold">Advanced Wi-Fi 7 Router</h2>
                    <p className="text-blue-100 mt-1">BE3600 Dual-Band Router - Optional Add-on</p>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-6 space-y-6">
                {/* Price */}
                <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4 text-center">
                  <p className="text-gray-700 font-semibold">Rental Price</p>
                  <p className="text-4xl font-bold text-airmax-blue">$10.00<span className="text-xl text-gray-600">/month</span></p>
                  <p className="text-sm text-gray-600 mt-1">Optional add-on • Waived with annual plans</p>
                </div>

                {/* Key Features */}
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">Why Upgrade?</h3>
                  <div className="space-y-4">
                    {/* Feature 1 */}
                    <motion.div
                      className="flex items-start space-x-3 bg-gray-50 p-4 rounded-lg"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1 }}
                    >
                      <CheckCircleIcon className="h-6 w-6 text-green-600 flex-shrink-0 mt-1" />
                      <div>
                        <h4 className="font-bold text-gray-900">Future-Ready Wi-Fi 7 Technology</h4>
                        <p className="text-gray-600 text-sm">Multi-Link Operation (MLO), Multi-RUs, and 4K-QAM for optimized performance with latest devices like iPhone 16 Pro and Samsung Galaxy S24 Ultra</p>
                      </div>
                    </motion.div>

                    {/* Feature 2 */}
                    <motion.div
                      className="flex items-start space-x-3 bg-gray-50 p-4 rounded-lg"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.2 }}
                    >
                      <CheckCircleIcon className="h-6 w-6 text-green-600 flex-shrink-0 mt-1" />
                      <div>
                        <h4 className="font-bold text-gray-900">6-Stream Dual-Band with 6.5 Gbps Total Bandwidth</h4>
                        <p className="text-gray-600 text-sm">Up to 5764 Mbps (5 GHz) + 688 Mbps (2.4 GHz) with 6 streams for seamless 4K/8K streaming, AR/VR gaming, and ultra-fast downloads</p>
                      </div>
                    </motion.div>

                    {/* Feature 3 */}
                    <motion.div
                      className="flex items-start space-x-3 bg-gray-50 p-4 rounded-lg"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.3 }}
                    >
                      <CheckCircleIcon className="h-6 w-6 text-green-600 flex-shrink-0 mt-1" />
                      <div>
                        <h4 className="font-bold text-gray-900">Wide Coverage with Strong Connection</h4>
                        <p className="text-gray-600 text-sm">Up to 2,400 sq. ft. coverage for up to 90 devices with 6 high-performance antennas and Beamforming technology</p>
                      </div>
                    </motion.div>

                    {/* Feature 4 */}
                    <motion.div
                      className="flex items-start space-x-3 bg-gray-50 p-4 rounded-lg"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.4 }}
                    >
                      <CheckCircleIcon className="h-6 w-6 text-green-600 flex-shrink-0 mt-1" />
                      <div>
                        <h4 className="font-bold text-gray-900">Ultra-Fast 2.5 Gbps Wired Performance</h4>
                        <p className="text-gray-600 text-sm">1× 2.5 Gbps WAN/LAN port, 1× 2.5 Gbps LAN port, and 3× 1 Gbps LAN ports for high-speed data transmissions</p>
                      </div>
                    </motion.div>
                  </div>
                </div>

                {/* Perfect For */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Perfect For:</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-airmax-blue rounded-full"></div>
                      <span className="text-gray-700">4K/8K Streaming</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-airmax-blue rounded-full"></div>
                      <span className="text-gray-700">Online Gaming</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-airmax-blue rounded-full"></div>
                      <span className="text-gray-700">Smart Home Devices</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-airmax-blue rounded-full"></div>
                      <span className="text-gray-700">Large Households</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-airmax-blue rounded-full"></div>
                      <span className="text-gray-700">Video Conferencing</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-airmax-blue rounded-full"></div>
                      <span className="text-gray-700">Multiple Users</span>
                    </div>
                  </div>
                </div>

                {/* CTA */}
                <div className="text-center pt-4">
                  <button
                    onClick={onClose}
                    className="bg-airmax-blue text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors shadow-lg"
                  >
                    Got It!
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default AdvancedRouterModal;
